import { useMemo, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button } from "reactstrap"

// Common Components
import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"

// Hooks
import { useListParams } from "hooks/useListParams"
import {
  useAccessoriesLists,
  useDeleteAccessoriesList,
} from "hooks/useAccessoriesLists"

// Utils
import { formatReadableDate } from "utils/formatDate"

// Local Components
import AccessoriesAddEdit from "./AccessoriesAddEdit"
import { AccessoriesHeader } from "./AccessoriesHeader"

const AccessoriesList = () => {
  document.title = "Accessories List"

  const navigate = useNavigate()

  // -------------------- STATE --------------------
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  // -------------------- LIST PARAMS --------------------
  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return { page, per_page, search }
  }, [params])

  // -------------------- API --------------------
  const {
    data: responseData,
    isLoading,
    refetch,
    isRefetching,
  } = useAccessoriesLists(queryParams)

  const { mutateAsync: deleteItem, isPending: deleteLoading } =
    useDeleteAccessoriesList()

  // -------------------- MODAL HANDLERS --------------------
  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Accessories deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (err) {
      console.error(err)
      toast.error("Failed to delete accessories.")
    }
  }

  // -------------------- TABLE COLUMNS --------------------
  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        size: 60,
        cell: ({ row }) => (
          <div className="text-muted fw-semibold">#{row.original.id}</div>
        ),
      },
      {
        header: "Accessory",
        accessorKey: "name",
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold">{row.original.name}</div>
          </div>
        ),
      },
      {
        header: "Purchased",
        accessorKey: "total_purchased",
        cell: ({ row }) => (
          <div className="fw-semibold">
            {row.original.total_purchased ?? 0}{" "}
            <small className="text-muted">
              {row.original.unit?.name || "-"}
            </small>
          </div>
        ),
      },
      {
        header: "Used",
        accessorKey: "total_used",
        cell: ({ row }) => (
          <div className="fw-semibold text-danger">
            {row.original.total_used ?? 0}{" "}
            <small>
              {row.original.unit?.name || "-"}
            </small>
          </div>
        ),
      },
      {
        header: "Available",
        accessorKey: "available_stock",
        cell: ({ row }) => {
          const stock = Number(row.original.available_stock)

          let badgeClass = "bg-success"
          let label = "In Stock"

          if (stock === 0) {
            badgeClass = "bg-warning text-dark"
            label = "Out of Stock"
          }

          if (stock < 0) {
            badgeClass = "bg-danger"
            label = "Negative"
          }

          return (
            <div>
              <span className={`badge ${badgeClass} px-3 py-1`}>
                {stock}{" "}
                <small>
                  {row.original.unit?.name || "-"}
                </small>
              </span>
              <div>
                <small className="text-muted">{label}</small>
              </div>
            </div>
          )
        },
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) => (
          <div className="text-muted" style={{ whiteSpace: "nowrap" }}>
            {row.original.created_at
              ? formatReadableDate(row.original.created_at)
              : "-"}
          </div>
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const data = row.original
          return (
            <div className="d-flex gap-1">
              <Button
                color="light"
                size="sm"
                onClick={() => navigate(`/accessories-list/${data.id}/view`)}
                id={`view-${data.id}`}
              >
                <i className="mdi mdi-eye" />
              </Button>
              <UncontrolledTooltip target={`view-${data.id}`}>
                View
              </UncontrolledTooltip>

              <Button
                color="info"
                size="sm"
                onClick={() => handleEditClick(data)}
                id={`edit-${data.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
              </Button>
              <UncontrolledTooltip target={`edit-${data.id}`}>
                Edit
              </UncontrolledTooltip>

              <Button
                color="danger"
                size="sm"
                onClick={() => handleDeleteClick(data)}
                id={`delete-${data.id}`}
              >
                <i className="mdi mdi-delete-outline" />
              </Button>
              <UncontrolledTooltip target={`delete-${data.id}`}>
                Delete
              </UncontrolledTooltip>
            </div>
          )
        },
      },
    ],
    []
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  // -------------------- RENDER --------------------
  return (
    <PageWrapper title="Accessories List" breadcrumbItem="Accessories List">
      <AccessoriesHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <DeleteModal
        show={deleteModal}
        loading={deleteLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="my-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        className="table-responsive" // ensures horizontal scroll on small screens
      />

      <AccessoriesAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={selectedData}
        toggleModal={toggleModal}
      />
    </PageWrapper>
  )
}

export default AccessoriesList
